import React, { useId, useState } from "react";
import { Alert, Box, Button, Checkbox, Chip, TextField, Typography } from "@mui/material";
import { RiCheckboxCircleLine, RiFlagLine, RiInformationLine } from "@remixicon/react";
import { MESSAGE_MAX } from "../../utils/ticketContract";
import { statusChip } from "../../utils/inboxPresentation";
import { personName } from "../../utils/studentDetail";
import { countText } from "../../utils/kitPresentation";
import { CREDIT_STEP, canResolve, creditValue, resolutionRows, resolutionText } from "../../utils/ticketDetail";
import { tokens } from "../../utils/figmaTokens";
import DetailRows from "../../components/kit/DetailRows";
import { Panel, PanelHead } from "./TicketParts";

// The stored resolution of a resolved or closed ticket: who, when, the credit and the reason.
const StoredResolution = ({ ticket, users, panelRef }) => {
  const chip = statusChip(ticket.status);
  return (
    <Panel ref={panelRef}>
      <PanelHead title="Resolution" aside={<Chip label={chip.label} color={chip.color} />} />
      {ticket.resolution ? (
        <>
          <DetailRows rows={resolutionRows(ticket, users)} line={tokens.divider} labelColor={tokens.text.secondary} />
          <Box sx={{ px: "12px", py: "10px", borderRadius: "8px", bgcolor: tokens.surface.sunken }}>
            <Typography variant="bodySmall" sx={{ color: tokens.text.primary, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {ticket.resolution.text}
            </Typography>
          </Box>
        </>
      ) : (
        <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
          No resolution was saved.
        </Typography>
      )}
    </Panel>
  );
};

// Resolution (D61; Resolution on 10829:93565), inline: a credit in half-session steps when a person
// raised the ticket, the reason they will read, what happens, and Resolve ticket. A resolved or
// closed ticket shows what was stored instead. `onResolve({ text, credit })` resolves to "" once
// resolveTicket has run, or to the error to show; what was typed stays until it succeeds.
// `panelRef` and `reasonRef` let the head's Resolve ticket bring the card into view.
const ResolutionCard = ({ ticket, users, onResolve, panelRef, reasonRef }) => {
  const creditId = useId();
  const person = Boolean(ticket.raisedBy);
  const [text, setText] = useState("");
  const [crediting, setCrediting] = useState(false);
  const [raw, setRaw] = useState("1");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!canResolve(ticket)) return <StoredResolution ticket={ticket} users={users} panelRef={panelRef} />;

  const amount = creditValue(raw);
  const credit = person && crediting ? amount : null;
  const words = resolutionText({ person, name: personName(users[ticket.raisedBy]), credit });
  const length = text.trim().length;
  const ready = length > 0 && length <= MESSAGE_MAX && !busy;

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setError("");
    const failure = await onResolve({ text: text.trim(), credit });
    setBusy(false);
    setError(failure || "");
  };

  return (
    <Panel ref={panelRef} strong>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", color: tokens.text.primary }}>
        <RiFlagLine size={18} aria-hidden />
        <Typography variant="titleCard" component="h2">
          Resolution
        </Typography>
      </Box>
      <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
        {words.intro}
      </Typography>
      {person && (
        <Box sx={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap", gap: "10px", py: "6px" }}>
          <Checkbox
            checked={crediting}
            onChange={(e) => setCrediting(e.target.checked)}
            disabled={busy}
            inputProps={{ "aria-labelledby": creditId }}
            sx={{ p: 0, color: tokens.border.strong, "&.Mui-checked": { color: tokens.brand.border } }}
          />
          <Box sx={{ flex: 1, minWidth: 180, display: "flex", flexDirection: "column", gap: "1px" }}>
            <Typography id={creditId} variant="bodySmallStrong" sx={{ color: tokens.text.primary }}>
              {words.creditLabel}
            </Typography>
            <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.secondary }}>
              In half-session steps
            </Typography>
          </Box>
          {crediting && (
            <TextField
              type="number"
              size="small"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              onBlur={() => setRaw(String(amount))}
              disabled={busy}
              inputProps={{ step: CREDIT_STEP, min: CREDIT_STEP, "aria-label": "Sessions to credit" }}
              sx={{ width: 120 }}
            />
          )}
        </Box>
      )}
      <TextField
        inputRef={reasonRef}
        multiline
        minRows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={words.hint}
        disabled={busy}
        error={length > MESSAGE_MAX}
        helperText={`${countText(length)} / ${countText(MESSAGE_MAX)}`}
        inputProps={{ "aria-label": "Reason" }}
        sx={{
          "& .MuiInputBase-root": { px: "12px", py: "10px", borderRadius: "8px", typography: "bodySmall" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.border.default },
          "& textarea::placeholder": { color: tokens.text.secondary, opacity: 1 },
        }}
      />
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px", px: "12px", py: "10px", borderRadius: "8px", bgcolor: tokens.info.bg, color: tokens.info.ink }}>
        <RiInformationLine size={16} aria-hidden style={{ flexShrink: 0, marginTop: 3 }} />
        <Typography variant="bodySmall">{words.happens}</Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button size="large" variant="contained" startIcon={<RiCheckboxCircleLine size={20} />} onClick={submit} disabled={!ready}>
          {busy ? "Resolving…" : "Resolve ticket"}
        </Button>
      </Box>
    </Panel>
  );
};

export default ResolutionCard;
